import React, { useState } from 'react';
import { getSupabaseClient } from '../lib/supabase';
import { Header, Footer } from '@ui';
import { useNavigate } from 'react-router-dom';

type EmailConfirmationProps = {
    email: string;
};

export default function EmailConfirmation({ email }: EmailConfirmationProps) {
    const [isResending, setIsResending] = useState(false);
    const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const navigate = useNavigate();
    const supabase = getSupabaseClient();

    const handleResendEmail = async () => {
        setIsResending(true);
        setResendStatus('idle');

        try {
            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: email,
            });

            if (error) throw error;
            setResendStatus('success');
        } catch (error) {
            console.error('Error resending confirmation email:', error);
            setResendStatus('error');
        } finally {
            setIsResending(false);
        }
    };

    const handleGoToLogin = () => {
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-[#FFFCEF] text-[#2C2731] flex flex-col">
            <Header variant="onboarding" />

            <main className="flex-1 flex items-center justify-center px-4 py-10">
                <div className="w-full max-w-2xl border border-[#2C2731] rounded-2xl bg-[#FFFCEF] p-8 shadow-sm">
                    {/* Icon */}
                    <div className="flex justify-center mb-6">
                        <div className="h-20 w-20 rounded-full bg-[#E8DFBD] border border-[#2C2731] flex items-center justify-center text-[#2C2731]">
                            <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                    </div>

                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-recoleta mb-4">Check your email</h1>
                        <p className="text-[#2C2731]/80 text-base">
                            We've sent a confirmation link to <span className="font-semibold">{email}</span>
                        </p>
                        <p className="text-[#2C2731]/70 text-sm mt-2">
                            Click the link in the email to verify your account and start your onboarding.
                        </p>
                    </div>

                    <div className="border border-[#2C2731] rounded-xl bg-[#E8DFBD] p-6 mb-6">
                        <h2 className="text-lg font-semibold text-[#2C2731] mb-3">What's next?</h2>
                        <ul className="space-y-3 text-sm text-[#2C2731]">
                            <li className="flex items-start gap-3">
                                <span className="font-semibold">1.</span>
                                <span>Check your inbox (and spam folder just in case)</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="font-semibold">2.</span>
                                <span>Click the confirmation link in the email</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="font-semibold">3.</span>
                                <span>You'll be redirected back to complete your guide onboarding</span>
                            </li>
                        </ul>
                    </div>

                    <div className="space-y-3">
                        <button
                            type="button"
                            className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#304031] text-sm font-semibold transition ${
                                isResending
                                    ? 'bg-[#304031]/70 text-[#fef6d6] cursor-not-allowed'
                                    : 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
                            }`}
                            onClick={handleResendEmail}
                            disabled={isResending}
                        >
                            {isResending ? 'Sending...' : 'Resend confirmation email'}
                        </button>

                        {resendStatus === 'success' && (
                            <div className="border border-[#2C2731] rounded-xl bg-[#E8DFBD] p-4 text-sm text-[#2C2731]">
                                ✓ Email sent! Check your inbox.
                            </div>
                        )}

                        {resendStatus === 'error' && (
                            <div className="border border-[#2C2731] rounded-xl bg-[#2C2731] p-4 text-sm text-[#FEF6D6]">
                                Failed to resend email. Please try again.
                            </div>
                        )}

                        <button
                            type="button"
                            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#304031] bg-transparent text-[#304031] text-sm font-semibold transition hover:bg-[#304031]/5"
                            onClick={handleGoToLogin}
                        >
                            Go to login
                        </button>
                    </div>

                    <div className="mt-6 border border-[#2C2731] rounded-xl bg-white p-4 text-sm text-[#2C2731]/80 text-center">
                        <div className="flex items-center justify-center gap-2">
                            <svg className="h-4 w-4 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Didn't receive the email? Check your spam folder or contact support.
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}
